import { ControlListProps, ControlListConfirmationDialogProps, useTranslations, useFormData, useRuntimeEnv, useTranslationLang } from "@bsm/component";
import { useEffect, useMemo } from "react";
import { AppointmentVacationBalance } from "../../vacation/components/AppointmentVacationBalance";
import { useTimeOffTypeList } from "../../../hooks/useTimeOffTypeList";
import _ from "lodash";
import moment from "moment";
import { useDateIncludeWorkingDays } from "hooks/useDateIncludeWorkingDays";
import { useWorkingDayListQuery } from "../../../../calendar/hooks/useWorkingDayListQuery";
import { TTimeOff } from "@bsm/graphql";
import { useDateDifferenceExcludingWeekends } from "hooks/useDateDifferenceExcludingWeekends";
import { useWorkingDaysInPeriod } from "../../leaving/hooks/useWorkingDaysInPeriod";
import { WorkingDayCheckControl } from "../../leaving/components/WorkingDayCheckControl";

export const useTimeOffMissingControlForm = (id?: number): ControlListProps => {
    const [
        DATE_BEGIN,
        DATE_END,
        HOUR_BEGIN, 
        HOUR_END, 
        SAVE, 
        CANCEL, 
        GUEST,  
        TITLE, 
        ATTACHMENT, 
        REASON, 
        BOARD, 
        LABELS,
        REPETITION,
        NOTIFY_BEFORE,
        TYPE_LABEL,
        TYPE_LEAVE,
        TYPE_ABSENCE,
        TYPE_SICK_LEAVE,
        TYPE_PERMISSION,
        APPROVE_LABEL,
        BALANCE,
        EMPLOYEE,
        END_DATE_ERROR,
        START_DATE_ERROR,
        INCLUDE_WORKING_DAYS_ERROR,
        CONFIRMATION_TITLE,
        CONFIRMATION_MESSAGE,
        CONFIRMATION,
        HOUR_END_SAME_ERROR,
        IS_CRITICAL_DAY
    ] = useTranslations(i18n);

    const { formData } = useFormData<Partial<TTimeOff>>({ id: TIMEOFF_MISSING_FORM_ADD_ID, formData: {}});
    const includeWorkingDays = useDateIncludeWorkingDays(formData?.employee?.id)
    
    const timeOffTypes = useTimeOffTypeList('MISSING')
    const getDateDifferenceExcludingWeekends = useDateDifferenceExcludingWeekends()
    const getWorkingDaysInPeriod = useWorkingDaysInPeriod();
    const { runtimeEnv } = useRuntimeEnv()
    const workingHourBegin = runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'
    const workingHourEnd = runtimeEnv?.organisation?.profile?.workHourEnd ?? '06:00'    
    const workRestHourEnd = runtimeEnv?.organisation?.profile?.workRestHourEnd ?? '13:00'

    const currentYear = moment(formData?.dateBegin).year() || moment().year()
    const { data: workingDaysData } = useWorkingDayListQuery(false, currentYear, formData?.employee?.id)

    let form = [
        {
            type: "hidden",
            name: "id",
        },
        [
            {
                type: "select",
                name: "employee",
                label: EMPLOYEE,
                valueObject: true,
                valueProperty: "id",
                GQLProps: {
                    gql: `
                        query {
                            contracts (filter:{data:{user:{department:{id:${(runtimeEnv?.user as any)?.department?.id ?? null}}}},sort: [{ sort: "ASC", value: "user.fullName" }] }) {
                                data{
                                    user {
                                        id fullName 
                                        profilePicture { id }
                                        department { id designation }
                                    }
                                }
                            }
                        }
                    `,
                    rootProp: "contracts.data",
                    labelProp: "user.fullName",
                    valueProp: "user.id",
                    userIdProp: "user.id",
                },
            },
            {
                type: "solde",
                name: "solde",
                label: BALANCE
            },
        ],
        [
            {
                type: "date-picker",
                name: "dateBegin",
                label: DATE_BEGIN,
                required: true,
                autoComplete: "off",
            },
            {
                type: "hour",
                name: "hourBegin",
                minHour: workingHourBegin,
                maxHour: workingHourEnd,
                value: workingHourBegin,
                interval: 60,
                label: HOUR_BEGIN,
            },
        ],
        [
            {
                type: "date-picker",
                name: "dateEnd",
                label: DATE_END,
                required: true,
            }, 
            {
                type: "hour",
                name: "hourEnd",
                options: [{ label: workingHourBegin, value: workingHourBegin }, { label: workRestHourEnd, value: workRestHourEnd }],
                value: workingHourBegin,
                label: HOUR_END,
            },
        ],
        {
            type: "textarea",
            name: "reason",
            label: REASON,
            required: true,
        },
        {
            type: "file",
            name: "files",
            label: ATTACHMENT,
            multiple: true,
        }
    ]

    const handleValidate = (name: string, value: any, formData_: any) => {
        
        if(name === "dateBegin") {
            const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
            // if (isCriticalDay) {
            //     return IS_CRITICAL_DAY
            // }
            if (!isWorkingDay) {
                return INCLUDE_WORKING_DAYS_ERROR;
            }

            const dateBeginIsAfterDateEnd = moment(formData_?.dateBegin, 'YYYY-MM-DD')?.isAfter(moment(formData_?.dateEnd, 'YYYY-MM-DD'))
            if(formData_?.dateBegin && dateBeginIsAfterDateEnd) {
                return START_DATE_ERROR
            }
        }
        if(name === "dateEnd") {
            const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
            // if (isCriticalDay) {
            //   return IS_CRITICAL_DAY
            // }
            if (!isWorkingDay) {
              return INCLUDE_WORKING_DAYS_ERROR;
            }

            const endDateIsBefore = moment(formData_?.dateEnd, 'YYYY-MM-DD').isBefore(moment(formData_?.dateBegin, 'YYYY-MM-DD'))
            if(formData_?.dateBegin && endDateIsBefore) {
                return END_DATE_ERROR
            }
        }

        if(name === "hourEnd") {
            const datesIsSame = moment(formData_?.dateEnd, 'YYYY-MM-DD').isSame(moment(formData_?.dateBegin, 'YYYY-MM-DD'))
            
            if (datesIsSame && formData_?.hourBegin === formData_?.hourEnd) {
                return HOUR_END_SAME_ERROR
            }
        }
    };

    return {
        data: {
            forms: form,
            fields: {
                "solde": AppointmentVacationBalance
            },
            value: {},
            next: {
                label: SAVE,
                className: "btn-primary ",
            },
            back: {
                label: CANCEL,
            },
            graphQL: {
                entity: "timeoff",
                entityId: id,
            }, 
        },
        validate: handleValidate,
        onBeforeSubmitDialog: (value) => {
            const dialogs: ControlListConfirmationDialogProps[] = [];

            if (!isNaN(Number(value?.solde))) {
                const requestedDays = getDateDifferenceExcludingWeekends(
                    moment(value?.dateBegin), moment(value?.dateEnd),
                    value?.hourBegin, value?.hourEnd
                );
                if (requestedDays > Number(value?.solde)) {
                    dialogs.push({
                        onClose: () => {},
                        onConfirm: () => {},
                        onCancel: () => {},
                        title: CONFIRMATION_TITLE,
                        message: CONFIRMATION_MESSAGE,
                    });
                }
            }

            if (workingDaysData?.data && formData?.dateBegin && formData?.dateEnd) {
                const workingDaysInPeriod = getWorkingDaysInPeriod(
                    workingDaysData?.data,
                    formData.dateBegin,
                    formData.dateEnd
                );

                if (workingDaysInPeriod.length > 0) {
                    dialogs.push({
                        onClose: () => {},
                        onConfirm: () => {},
                        onCancel: () => {},
                        title: CONFIRMATION,
                        confirmationForm: {
                            forms: [{
                            type: "working_days_check",
                            name: "accountableWorkingDays",
                            workingDays: workingDaysInPeriod
                            }],
                            fields: { "working_days_check": WorkingDayCheckControl }
                        }
                    });
                }
            }

            return dialogs.length > 0 ? dialogs : null;
        },
        onBeforeSaving: (value) => {
            const type = timeOffTypes?.find(type => type.value === 'ABSENCE')
            let value_ = {
                ..._.clone(value),
                type: { id: type.id }
            } as typeof value;

            value_.hourBegin = value?.hourBegin ? value?.hourBegin : runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'
            value_.hourEnd = value?.hourEnd ? value?.hourEnd : runtimeEnv?.organisation?.profile?.workHourBegin ?? '06:00'

            if (value?.confirmationData?.accountableWorkingDays) {
              value_.accountableWorkingDays = JSON.stringify(value?.confirmationData?.accountableWorkingDays);
            }
 
            if(!id) {
                value_.status = 'new'
            }
            return value_;
        },
        id: TIMEOFF_MISSING_FORM_ADD_ID
    };
}

const TIMEOFF_MISSING_FORM_ADD_ID = "timeoff-add-missing-form"

const i18n = [
    "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentBegin",
    "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentEnd",
    "std_save",
    "std_cancel",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Guest",
    "std_title",
    "std_justification",
    "std_reason",
    "std_board",
    "std_labels",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.Repetition",
    "Harea.Vacation.Calendar.Event.Create.Vacation.Form.Label.NotifyBefore",
    "std_type",
    "std_leave",
    "std_absence",
    "std_sick_leave",
    "std_permission",
    "std_approve",
    "std_remaining_balance",
    "std_employee",
    "BSMRH.Calendar.TimeOff.Create.Form.EndDate.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.StartDate.Error.Message",
    "BSMRH.Calendar.TimeOff.Create.Form.IncludeWorkingDays.Error.Message",
    "std_confirmation",
    "BSMRH.Calendar.TimeOff.Create.Form.InsufficientBalance.Message",
    "std_confirmation",
    "BSMRH.Calendar.TimeOff.Create.Form.HourEnd.Same.Error",
    "BSMRH.Calendar.TimeOff.Create.Form.IsCriticalDay.Error.Message",
];